import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { XML_ABOUT } from '../content/toolGuides';
import { XML_ENCODING_ROUTE } from '../content/tools';
import { useTextConverter } from '../hooks/useTextConverter';

const xmlEntities: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&apos;',
};

const reverseXmlEntities: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
};

const encodeXml = (input: string) => {
  let encoded = input;
  for (const [char, entity] of Object.entries(xmlEntities)) {
    encoded = encoded.split(char).join(entity);
  }
  return encoded;
};

const decodeXml = (input: string) => {
  let decoded = input;
  // Sort by length descending to handle &amp; before &
  const sortedEntities = Object.entries(reverseXmlEntities).sort((a, b) => b[0].length - a[0].length);
  for (const [entity, char] of sortedEntities) {
    decoded = decoded.split(entity).join(char);
  }
  return decoded;
};

const XmlEncoding: React.FC = () => {
  const converter = useTextConverter({
    modes: ['encode', 'decode'] as const,
    transforms: { encode: encodeXml, decode: decodeXml },
    errors: {
      encode: 'Failed to encode XML.',
      decode: 'Failed to decode XML.',
    },
  });
  const { mode } = converter;

  return (
    <>
      <RouteSeo route={XML_ENCODING_ROUTE} />
      <ToolLayout
        title="XML Encoding / Decoding"
        description="Encode or decode special characters for use in XML documents"
        icon={faCode}
        controls={
          <div className="tool-controls__group">
            <Form.Check
              type="radio"
              id="encode-radio-xml"
              label="Encode"
              checked={mode === 'encode'}
              onChange={() => {
                converter.setMode('encode');
                converter.clearError();
              }}
              inline
            />
            <Form.Check
              type="radio"
              id="decode-radio-xml"
              label="Decode"
              checked={mode === 'decode'}
              onChange={() => {
                converter.setMode('decode');
                converter.clearError();
              }}
              inline
            />
          </div>
        }
        error={converter.error}
        onDismissError={converter.clearError}
        inputLabel={mode === 'encode' ? 'Plain Text' : 'XML-Encoded Text'}
        inputValue={converter.input}
        onInputChange={converter.setInput}
        inputPlaceholder={mode === 'encode' ? 'Enter text to encode...' : 'Enter XML-encoded text to decode...'}
        inputRows={10}
        actions={
          <>
            <Button variant="primary" onClick={converter.convert} disabled={!converter.input}>
              <FontAwesomeIcon icon={faArrowRight} className="me-2" />
              {mode === 'encode' ? 'Encode' : 'Decode'}
            </Button>
            <Button variant="outline-secondary" onClick={converter.switchMode} disabled={!converter.output}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Switch
            </Button>
          </>
        }
        outputLabel={mode === 'encode' ? 'XML-Encoded Text' : 'Plain Text'}
        outputValue={converter.output}
        outputRows={10}
        onCopy={converter.copy}
        copied={converter.copied}
        aboutTitle="About XML Encoding"
        about={XML_ABOUT}
      >
        <ToolContent route={XML_ENCODING_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default XmlEncoding;
